/**
 * Grand Quack Auto — QUACK system.
 *
 * THE single most important mechanic — see research.md §6.
 * Spacebar → expanding ring VFX + NPC scare broadcast + chaos points.
 * Cooldown prevents spam. Ring is purely visual (no physics collider).
 *
 * See research.md §5 session 4 prompt.
 */

import * as THREE from 'three';
import { DUCKLING, NPC, PALETTE, QUACK, VOICE } from '../config';
import type { System, World } from '../types';
import { getHtmlVolume } from '../utils/audioMixer';
import { lerp } from '../utils/mathUtils';
import { playMegaVoiceLine, playVoiceLine } from '../utils/voiceSynth';
import { addChaos } from './chaosSystem';
import { isMotherEnraged, recallDucklings, spawnDuckling } from './ducklingSystem';
import { triggerHaze, triggerShake, triggerSlowMo } from './juiceSystem';
import { scareNearbyNPCs, triggerRagdollLaunch } from './npcSystem';
import { showPopup } from '../ui/scorePopups';

// ── Ring VFX state ────────────────────────────────────────────────────────

interface ActiveRing {
  mesh: THREE.Mesh<THREE.RingGeometry, THREE.MeshBasicMaterial>;
  startTime: number;
}

const activeRings: ActiveRing[] = [];

// Shared geometry — created once, reused by all rings.
let sharedRingGeom: THREE.RingGeometry | null = null;
function getRingGeom(): THREE.RingGeometry {
  if (!sharedRingGeom) sharedRingGeom = new THREE.RingGeometry(0.8, 1.0, 32);
  return sharedRingGeom;
}

// ── Real duck quack SFX (Pixabay, free for commercial use) ────────────────

const quackAudio = new Audio('/sounds/quack.mp3');

function playQuackSound(): void {
  quackAudio.volume = getHtmlVolume(0.8);
  quackAudio.currentTime = 0;
  quackAudio.play().catch(() => {});
}

// ── Mega-Quack ────────────────────────────────────────────────────────────

const MEGA_CHARGE_TIME = 1.2; // seconds to fully charge
const MEGA_RADIUS_MULT = 3;  // 3x normal scare radius

// ── Cooldown ──────────────────────────────────────────────────────────────

let lastProcessedQuackAt = -Infinity;
let wasCharging = false;

// ── System ────────────────────────────────────────────────────────────────

export const quackSystem: System = {
  name: 'quack',

  fixedUpdate(_dt: number, world: World) {
    // Mega-Quack: duck scales up while charging Space.
    const charge = world.input.quackCharge;
    const isCharging = charge > 0.2; // ignore taps under 200ms
    if (isCharging && world.playerMode === 'foot') {
      const t = Math.min(charge / MEGA_CHARGE_TIME, 1);
      world.duck.scale.setScalar(1 + t * 0.5); // scale 1→1.5x
    }

    // Detect Space release after charging.
    const megaRelease = wasCharging && !isCharging && charge === 0;
    wasCharging = isCharging;

    if (!world.input.quack && !megaRelease) return;
    const enraged = isMotherEnraged(world);
    const cooldown = enraged ? QUACK.cooldownSeconds * DUCKLING.rageCooldownMult : QUACK.cooldownSeconds;
    if (world.time.elapsed - lastProcessedQuackAt < cooldown) return;

    // Determine if this is a mega-quack.
    const isMega = megaRelease;
    lastProcessedQuackAt = world.time.elapsed;
    world.duck.scale.setScalar(1); // reset charge scale

    // Spawn ring VFX at duck's current position.
    const material = new THREE.MeshBasicMaterial({
      color: PALETTE.duck,
      transparent: true,
      opacity: 0.6,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(getRingGeom(), material);
    mesh.rotation.x = -Math.PI / 2; // flat on XZ plane
    mesh.position.copy(world.duck.position);
    mesh.position.y = 0.05; // just above ground
    mesh.scale.set(0, 0, 0); // start invisible, scale up in render
    world.scene.add(mesh);
    activeRings.push({ mesh, startTime: world.time.elapsed });

    // QUACK! (or MEGA QUAAACK!)
    playQuackSound();
    if (isMega) {
      playMegaVoiceLine();
      triggerShake(0.8);
      triggerSlowMo(world);
      triggerHaze();
      showPopup(world, 'mega_MEGA QUAAACK!');
    } else {
      if (Math.random() < VOICE.chance) playVoiceLine();
      triggerShake(0.3);
    }

    // Scare nearby NPCs — mega has 3x radius, rage has 1.5x.
    const baseRadius = QUACK.scareRadius * (enraged ? DUCKLING.rageScareRadiusMult : 1);
    const radius = isMega ? baseRadius * MEGA_RADIUS_MULT : baseRadius;
    scareNearbyNPCs(world, world.duck.position, radius);

    // Mega-quack also LAUNCHES nearby NPCs into ragdoll arcs (Phase B2).
    // The visual punchline: a duck sending humans flying with a quack.
    if (isMega) {
      const launched = triggerRagdollLaunch(
        world, world.duck.position, NPC.megaLaunchRadius,
      );
      for (let i = 0; i < launched.length; i++) addChaos(world, 'knockdown');
      if (launched.length > 0) showPopup(world, 'launched');
    }

    // Brood mechanics: hatch a duckling, recall any defenders.
    for (let i = 0; i < DUCKLING.spawnPerQuack; i++) spawnDuckling(world);
    recallDucklings(world);
  },

  render(_dt: number, _alpha: number, world: World) {
    // Animate active rings: expand + fade, remove when done.
    for (let i = activeRings.length - 1; i >= 0; i--) {
      const ring = activeRings[i]!;
      const t = (world.time.elapsed - ring.startTime) / QUACK.ringDurationSeconds;

      if (t >= 1) {
        // Ring finished — clean up.
        world.scene.remove(ring.mesh);
        ring.mesh.material.dispose();
        activeRings.splice(i, 1);
        continue;
      }

      // Scale up from 0 → QUACK.radius, fade opacity from 0.6 → 0.
      const s = lerp(0, QUACK.radius, t);
      ring.mesh.scale.set(s, s, s);
      ring.mesh.material.opacity = lerp(0.6, 0, t);
    }
  },
};
